import { useState, useEffect, useContext, createContext } from 'react';
import { Auth } from 'aws-amplify';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { logger } from '../utils/logger';
import { apmBase } from '@elastic/apm-rum';
import { GET_ROLES } from '../queries/roles';

const UserContext = createContext();

const environment = process.env.MCP_ENV || 'local';
const isProduction = ['staging', 'prod', 'production'].includes(
  environment.toLowerCase()
);

const userHasMcpAdminAccess = (user) => {
  if (!user) return false;
  if (!isProduction) return true;
  const roles = user.attributes['custom:roles'] || [];
  return roles.includes('MCPAdminUser');
};

const hasChannelSurveyAccess = (roles) =>
  !!(roles && roles.includes('admin') && roles.includes('revenue'));

export { UserProvider, UserContext, useUser, hasChannelSurveyAccess };

const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  if (error) {
    throw error;
  }

  useEffect(() => {
    async function getUser() {
      let user = {};
      try {
        user = await Auth.currentAuthenticatedUser();

        const idTokenPayload = user.signInUserSession?.idToken?.payload;
        const identityProvider = idTokenPayload?.identities?.[0]?.providerName;
        if (identityProvider !== process.env.FEDERATED_AUTH_PROVIDER) {
          logger.logError(
            `User authenticated with wrong provider: ${identityProvider}. Re-authenticating with ${process.env.FEDERATED_AUTH_PROVIDER}.`
          );
          await Auth.signOut();
          Auth.federatedSignIn({
            customProvider: process.env.FEDERATED_AUTH_PROVIDER,
          });
          return;
        }

        if (!userHasMcpAdminAccess(user)) {
          throw new Error('User is missing application access!');
        }
        setUser(user);
      } catch (error) {
        if (error === 'The user is not authenticated') {
          Auth.federatedSignIn({
            customProvider: process.env.FEDERATED_AUTH_PROVIDER,
          });
        } else {
          logger.logError(error);
          setError(error);
        }
      }
    }
    if (!user) {
      getUser();
    }
  }, [user]);

  const name = user?.attributes['custom:fullName'] || '';

  // Query for Permit.io roles once the user is authenticated
  const { data: rolesData } = useQuery(GET_ROLES, {
    skip: !user || !userHasMcpAdminAccess(user),
    fetchPolicy: 'network-only',
  });

  let roles = rolesData?.getRoles || [];

  const userAttributes = {
    id: user?.id || '',
    roles: roles,
    name: name,
    username: name,
    email: user?.attributes['email'] || '',
  };

  if (userAttributes.email) {
    apmBase?.setUserContext({
      email: userAttributes.email,
      id: userAttributes.id,
      username: userAttributes.username,
    });
    apmBase?.setCustomContext({
      roles: userAttributes.roles,
    });
  }

  return (
    <UserContext.Provider
      value={{
        cognitoData: user || {},
        isLoggedIn: !!user && userHasMcpAdminAccess(user),
        ...userAttributes,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

UserProvider.propTypes = {
  children: PropTypes.node,
};
